import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { BiSearch, BiSolidInbox } from 'react-icons/bi';
import { listStudentAffairs } from '@/api/studentAffairs.api';
import {
  DataTable,
  type ActiveFilterChip,
  type DataTableColumn,
  type DataTableSortState,
} from '@/components/table/DataTable';
import { EmptyState } from '@/components/table/EmptyState';
import { StatusChip } from '@/components/common/StatusChip';
import { ProgressBar } from '@/components/common/ProgressBar';
import { formatDateTime } from '@/lib/format';
import { toneForStudentStatus } from '@/lib/studentStatus';
import { STUDENT_STATUSES, TRANSFER_STATUSES } from '@/types/shared';
import type {
  PaginatedStudentAffairs,
  StudentAffairsResponse,
  StudentStatus,
  TransferStatus,
} from '@/types/shared';
import './StudentAffairsListPage.css';

export function StudentAffairsListPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useState<DataTableSortState>({ field: null, direction: 'asc' });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StudentStatus | ''>('');
  // T184 (§21) — mirrors `statusFilter` above for the new, separate `transferStatus` field.
  const [transferStatusFilter, setTransferStatusFilter] = useState<TransferStatus | ''>('');

  const [data, setData] = useState<PaginatedStudentAffairs | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  // Debounce the free-text search box before it drives the `search` query param.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Any filter/search change re-starts pagination at page 1 (sort changes keep the current page).
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, transferStatusFilter]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const sortParam = sort.field ? `${sort.field}:${sort.direction.toUpperCase()}` : undefined;

    listStudentAffairs({
      page,
      pageSize,
      sort: sortParam,
      search: search || undefined,
      studentStatus: statusFilter || undefined,
      transferStatus: transferStatusFilter || undefined,
    })
      .then((result) => {
        if (cancelled) return;
        setData(result);
      })
      .catch(() => {
        if (cancelled) return;
        setError(t('studentAffairs.error.loadFailed'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [page, pageSize, sort, search, statusFilter, transferStatusFilter, t, reloadToken]);

  const activeFilterCount = [
    Boolean(search),
    statusFilter !== '',
    transferStatusFilter !== '',
  ].filter(Boolean).length;
  const hasActiveFilters = activeFilterCount > 0;

  function clearFilters() {
    setSearchInput('');
    setSearch('');
    setStatusFilter('');
    setTransferStatusFilter('');
  }

  const activeFilterChips: ActiveFilterChip[] = [
    statusFilter !== '' && {
      key: 'status',
      label: t('studentAffairs.filters.status'),
      valueLabel: t(`enums.studentStatus.${statusFilter}`),
      onRemove: () => setStatusFilter(''),
    },
    transferStatusFilter !== '' && {
      key: 'transferStatus',
      label: t('studentAffairs.filters.transferStatus'),
      valueLabel: t(`enums.transferStatus.${transferStatusFilter}`),
      onRemove: () => setTransferStatusFilter(''),
    },
  ].filter(Boolean) as ActiveFilterChip[];

  const columns: DataTableColumn<StudentAffairsResponse>[] = [
    {
      key: 'studentCode',
      header: t('studentAffairs.columns.studentCode'),
      sortField: 'studentCode',
      // Primary identifier column — always rendered, never hidden (14-...-spec.md §4.2).
      toggleable: false,
      render: (row) => (
        <Link
          to={`/student-affairs/${row.student.studentCode}`}
          className="fa-code-pill fa-ltr-isolate"
        >
          {row.student.studentCode}
        </Link>
      ),
    },
    {
      key: 'nameAr',
      header: t('studentAffairs.columns.nameAr'),
      sortField: 'nameAr',
      render: (row) => row.student.nameAr,
    },
    {
      key: 'nameEn',
      header: t('studentAffairs.columns.nameEn'),
      sortField: 'nameEn',
      render: (row) => row.student.nameEn,
    },
    {
      key: 'studentStatus',
      header: t('studentAffairs.columns.studentStatus'),
      sortField: 'studentStatus',
      render: (row) =>
        row.studentStatus ? (
          <StatusChip
            label={t(`enums.studentStatus.${row.studentStatus}`)}
            tone={toneForStudentStatus(row.studentStatus)}
          />
        ) : (
          <span className="fa-student-affairs-list__muted">{t('studentAffairs.notSet')}</span>
        ),
    },
    {
      key: 'transferStatus',
      header: t('studentAffairs.columns.transferStatus'),
      // No `sortField` — the backend's `applySort` allow-list (student-affairs.service.ts) does
      // not include `transferStatus`, unlike `studentStatus` above.
      // Blank when null (per T184 spec) — unlike the studentStatus column above, no "Not set"
      // placeholder text or chip styling.
      render: (row) => (row.transferStatus ? t(`enums.transferStatus.${row.transferStatus}`) : ''),
    },
    {
      key: 'documents',
      header: t('studentAffairs.columns.documents'),
      render: (row) => (
        <span className="fa-student-affairs-list__progress-cell">
          <ProgressBar
            value={row.documentsReceived}
            max={row.documentsTotal}
            label={t('studentAffairs.documentsValue', {
              received: row.documentsReceived,
              total: row.documentsTotal,
            })}
          />
          <bdi>
            {t('studentAffairs.documentsValue', {
              received: row.documentsReceived,
              total: row.documentsTotal,
            })}
          </bdi>
        </span>
      ),
    },
    {
      key: 'audit',
      header: t('studentAffairs.columns.audit'),
      sortField: 'createdAt',
      render: (row) => (
        <span className="fa-student-affairs-list__audit">
          <span className="fa-student-affairs-list__audit-name">
            {row.updatedBy?.fullName ?? t('studentAffairs.unknownUser')}
          </span>
          <bdi className="fa-student-affairs-list__audit-date">
            {formatDateTime(row.updatedAt, i18n.language)}
          </bdi>
        </span>
      ),
    },
  ];

  return (
    <section className="fa-student-affairs-list">
      <div className="fa-student-affairs-list__header">
        <div>
          <h1 className="fa-student-affairs-list__title">{t('studentAffairs.listTitle')}</h1>
          <p className="fa-student-affairs-list__subtitle">
            {data ? t('studentAffairs.resultCount', { count: data.meta.total }) : ' '}
          </p>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.student.studentCode}
        caption={t('studentAffairs.tableCaption')}
        meta={data?.meta ?? null}
        loading={loading}
        error={error}
        onRetry={() => setReloadToken((v) => v + 1)}
        onRowClick={(row) => navigate(`/student-affairs/${row.student.studentCode}`)}
        search={{
          value: searchInput,
          onChange: setSearchInput,
          placeholder: t('studentAffairs.searchPlaceholder'),
          ariaLabel: t('studentAffairs.searchAriaLabel'),
        }}
        sort={sort}
        onSortChange={setSort}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        exportConfig={{
          endpoint: '/student-affairs/export',
          // Mirrors the list call's active filters, minus page/pageSize.
          params: {
            sort: sort.field ? `${sort.field}:${sort.direction.toUpperCase()}` : undefined,
            search: search || undefined,
            studentStatus: statusFilter || undefined,
            transferStatus: transferStatusFilter || undefined,
          },
          fallbackFilename: 'StudentAffairs_export.xlsx',
        }}
        columnVisibility={{
          storageKey: 'student-affairs-table-columns',
          ariaLabel: t('table.columns.toggleButtonAriaLabel'),
          drawerTitle: t('table.columns.drawerTitleDefault'),
        }}
        activeFilters={activeFilterChips}
        onClearAllFilters={clearFilters}
        filtersDrawerTitle={t('studentAffairs.filters.groupLabel')}
        filtersSlot={
          <div className="fa-student-affairs-list__filters-group">
            <label className="fa-student-affairs-list__filter">
              <span className="fa-student-affairs-list__filter-label">
                {t('studentAffairs.filters.status')}
              </span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StudentStatus | '')}
              >
                <option value="">{t('studentAffairs.filters.allStatuses')}</option>
                {STUDENT_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {t(`enums.studentStatus.${status}`)}
                  </option>
                ))}
              </select>
            </label>
            <label className="fa-student-affairs-list__filter">
              <span className="fa-student-affairs-list__filter-label">
                {t('studentAffairs.filters.transferStatus')}
              </span>
              <select
                value={transferStatusFilter}
                onChange={(e) => setTransferStatusFilter(e.target.value as TransferStatus | '')}
              >
                <option value="">{t('studentAffairs.filters.allTransferStatuses')}</option>
                {TRANSFER_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {t(`enums.transferStatus.${status}`)}
                  </option>
                ))}
              </select>
            </label>
          </div>
        }
        emptyState={
          hasActiveFilters ? (
            <EmptyState
              icon={BiSearch}
              title={t('studentAffairs.empty.noMatchesTitle')}
              hint={t('studentAffairs.empty.noMatchesHint')}
              action={
                <button type="button" className="fa-btn fa-btn--secondary" onClick={clearFilters}>
                  {t('studentAffairs.filters.clear')}
                </button>
              }
            />
          ) : (
            <EmptyState
              icon={BiSolidInbox}
              title={t('studentAffairs.empty.noRecordsTitle')}
              hint={t('studentAffairs.empty.noRecordsHint')}
            />
          )
        }
      />
    </section>
  );
}
